import React, { useMemo } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { MedicalNotesIcon } from "../../../Icons";
import { carePlanTemplateApi } from "../../../services/librariesCatalogService";
import LibraryCatalogPage, {
  cellMuted,
  cellStrong,
} from "../components/LibraryCatalogPage";
import { LIBRARIES_BTN } from "../librariesTheme";
import CarePlanTemplateFormModal from "./CarePlanTemplateFormModal";
import {
  getCarePlanSection,
  isCarePlanMetaSection,
} from "./carePlanSections";

const CarePlanSectionPage = () => {
  const { sectionKey } = useParams();
  const section = getCarePlanSection(sectionKey);
  const isMeta = isCarePlanMetaSection(sectionKey);

  const extraListParams = useMemo(
    () => (sectionKey ? { sectionKey } : null),
    [sectionKey],
  );

  const FormModal = useMemo(() => {
    const Bound = (props) => (
      <CarePlanTemplateFormModal
        {...props}
        sectionKey={sectionKey}
        sectionLabel={section?.label || "Template"}
      />
    );
    Bound.displayName = "BoundCarePlanTemplateFormModal";
    return Bound;
  }, [sectionKey, section?.label]);

  const columns = useMemo(() => {
    const cols = [
      {
        header: section?.label || "Template",
        accessor: "templateText",
        sortable: true,
        className: "px-3 min-w-[14rem] max-w-xl",
        render: (row) => (
          <span className={`${cellStrong} whitespace-normal line-clamp-2`}>
            {row.templateText || "—"}
          </span>
        ),
      },
    ];
    if (!isMeta) {
      cols.push({
        header: "Category",
        accessor: "category",
        sortable: true,
        className: "whitespace-nowrap px-3",
        render: (row) => (
          <span className={cellMuted}>{row.category || "—"}</span>
        ),
      });
    }
    cols.push({
      header: "Display Order",
      accessor: "displayOrder",
      sortable: true,
      className: "whitespace-nowrap px-3",
      render: (row) => (
        <span className={`${cellMuted} tabular-nums`}>
          {row.displayOrder == null ? "—" : row.displayOrder}
        </span>
      ),
    });
    return cols;
  }, [section?.label, isMeta]);

  if (!section) {
    return <Navigate to="/libraries/care-plan-templates" replace />;
  }

  return (
    <div className="space-y-3">
      <nav className="text-xs text-slate-500" aria-label="Breadcrumb">
        <Link
          to="/libraries/care-plan-templates"
          className="text-indigo-700 hover:underline font-medium"
        >
          Care Plan Library
        </Link>
        <span className="mx-1.5 text-slate-300">/</span>
        <span className="text-slate-700 font-semibold">{section.label}</span>
      </nav>

      <LibraryCatalogPage
        title={section.label}
        icon={MedicalNotesIcon}
        addLabel={`Create ${section.label}`}
        searchPlaceholder={`Search ${section.label.toLowerCase()} templates…`}
        entityLabel={section.label}
        defaultSortBy="templateText"
        api={carePlanTemplateApi}
        columns={columns}
        FormModal={FormModal}
        extraListParams={extraListParams}
        toolbarExtra={
          <Link
            to="/libraries/care-plan-templates"
            className={LIBRARIES_BTN.outline}
          >
            All sections
          </Link>
        }
        getDeleteLabel={(row) =>
          (row.templateText || "").slice(0, 80) || section.label
        }
      />
    </div>
  );
};

export default CarePlanSectionPage;
